import { UntilDestroy } from '@ngneat/until-destroy';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectPageRouteMap } from 'sharedStores/cms/cms.selectors';
import { Route } from 'dataModels/route';
import { PageTypeService } from 'services/page-type.service';
import { PageType } from 'sharedEnums/page-type';
import { PageRouteMapKeys } from 'sharedModels/page-route-map';
import { selectPreviousPageIndex } from 'shared/base/generics/store/quote-controller/quote-controller.selectors';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class EmploymentStatusNavigationService {
    /**
     * Constructor for the EmploymentStatusNavigationService.
     * @param store$ - The NgRx store used to select state signals.
     * @param pageTypeService - Service for handling page type related logic.
     */
    public constructor(
        private readonly store$: Store,
        private readonly pageTypeService: PageTypeService,
    ) {}

    /**
     * Signals for retrieving navigation data from the store.
     */
    private readonly pageRouteMap = this.store$.selectSignal(selectPageRouteMap);
    private readonly previousPageIndex = this.store$.selectSignal(selectPreviousPageIndex());

    /**
     * Resolves the conditional button route for the policyholder based on the page name and route.
     * Handles "EmploymentStatus" and "HouseholdMemberEmploymentStatus" page names.
     * @param pageName - The name of the page to resolve the route for.
     * @param route - The default route to be used.
     * @returns The resolved route based on employment status and page type.
     */
    public resolveConditionalButtonRoutePolicyHolder(pageName: string, route: Route): Route {
        const map = this.pageRouteMap();
        let pageType: PageType | null = null;
        let unemployedRoute: Route | null = null;

        // Check if this is a back navigation request
        if (this.isBackNavigation(route)) {
            // Handle back navigation - force route to marital status for employment status page
            if (pageName === PageRouteMapKeys.HouseholdMemberEmploymentStatus) {
                return map[PageRouteMapKeys.HouseholdMemberMaritalStatus] || route;
            }
        }

        // Determine the page type and routes based on the page name for forward navigation
        switch (pageName) {
            case PageRouteMapKeys.HouseholdMemberEmploymentStatus:
                pageType = PageType.HouseholdMember;
                unemployedRoute = map[PageRouteMapKeys.HouseholdMemberUnemployedStatus];
                route = map[PageRouteMapKeys.HouseholdMemberLicenceType];
                break;
            case PageRouteMapKeys.EmploymentStatus:
                pageType = PageType.PolicyHolder;
                unemployedRoute = map[PageRouteMapKeys.UnemployedStatus];
                break;
            default:
                return route;
        }

        // Retrieve person data based on the page type.
        const personData = this.store$.selectSignal(this.pageTypeService.selectPerson(pageType))();
        const employmentStatus = personData?.person?.employmentStatus?.code;

        // Route based on employment status
        if (employmentStatus == 'UU') {
            route = unemployedRoute;
        }

        return route;
    }

    /**
     * Determines if the current navigation request is a back navigation.
     * @param route - The route being processed.
     * @returns True if this appears to be back navigation.
     */
    private isBackNavigation(route: Route): boolean {
        const previousPage = this.previousPageIndex();
        return previousPage?.route?.pageRoute === route?.pageRoute;
    }
}
